"use client";

import {
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  type FirestoreDataConverter,
  type DocumentData,
  Timestamp,
} from "firebase/firestore";
import type {
  V3Game,
  V3Person,
  V3Organization,
  V3Team,
  V3Location,
  V3Expense,
  V3MileageTrip,
  V3TaxYearConfig,
  V3VoiceNote,
  V3ImportJob,
  V3ExportRecord,
  V3UserProfile,
} from "../types.v3";

function withId<T extends { id: string }>(
  snap: QueryDocumentSnapshot,
  opts?: SnapshotOptions,
): T {
  const data = snap.data(opts) as Omit<T, "id">;
  return { ...data, id: snap.id } as T;
}

function stripId<T extends { id: string }>(obj: T): Omit<T, "id"> {
  const { id: _id, ...rest } = obj;
  return rest;
}

function makeConverter<T extends { id: string }>(): FirestoreDataConverter<T> {
  return {
    toFirestore(model: T): DocumentData {
      return stripId(model) as DocumentData;
    },
    fromFirestore(
      snap: QueryDocumentSnapshot,
      options?: SnapshotOptions,
    ): T {
      return withId<T>(snap, options);
    },
  };
}

export const gameConverter = makeConverter<V3Game>();
export const personConverter = makeConverter<V3Person>();
export const organizationConverter = makeConverter<V3Organization>();
export const teamConverter = makeConverter<V3Team>();
export const locationConverter = makeConverter<V3Location>();
export const expenseConverter = makeConverter<V3Expense>();
export const mileageTripConverter = makeConverter<V3MileageTrip>();
export const taxYearConfigConverter: FirestoreDataConverter<V3TaxYearConfig & { id: string }> = {
  toFirestore(model: V3TaxYearConfig & { id: string }): DocumentData {
    const { id: _id, ...rest } = model;
    return rest as DocumentData;
  },
  fromFirestore(snap: QueryDocumentSnapshot, options?: SnapshotOptions): V3TaxYearConfig & { id: string } {
    const data = snap.data(options) as V3TaxYearConfig;
    return { ...data, id: snap.id };
  },
};
export const voiceNoteConverter = makeConverter<V3VoiceNote>();
export const importJobConverter = makeConverter<V3ImportJob>();
export const exportRecordConverter = makeConverter<V3ExportRecord>();
export const userProfileConverter: FirestoreDataConverter<V3UserProfile & { id: string }> = {
  toFirestore(model: V3UserProfile & { id: string }): DocumentData {
    const { id: _id, ...rest } = model;
    return rest as DocumentData;
  },
  fromFirestore(snap: QueryDocumentSnapshot, options?: SnapshotOptions): V3UserProfile & { id: string } {
    const data = snap.data(options) as V3UserProfile;
    return { ...data, id: snap.id };
  },
};

export function serverTimestamp(): Timestamp {
  return Timestamp.now();
}
